import { Plus } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { moveAnnouncement, movePosition } from "@/lib/documents";
import type { DocumentDetail, Position } from "@/lib/types";
import type { ItemsKind } from "./ItemsPanel";
import { PositionRow } from "./PositionRow";
import type { DocumentWrites } from "./useDocument";

function moved(ids: number[], id: number, to: number): number[] {
  const rest = ids.filter((x) => x !== id);
  rest.splice(Math.max(0, Math.min(rest.length, to)), 0, id);
  return rest;
}

export function PositionList({
  detail, writes, offerNumber, highlighted, onHover, onFocusChange, onOpenItems,
}: {
  detail: DocumentDetail;
  writes: DocumentWrites;
  offerNumber: string | null;
  highlighted: number | null;
  onHover: (id: number | null) => void;
  onFocusChange: (id: number | null) => void;
  onOpenItems: (kind: ItemsKind) => void;
}) {
  const doc = detail.document;
  // While a new order is being saved the list shows it already.
  const [pendingOrder, setPendingOrder] = useState<number[] | null>(null);
  // The newest order asked for, ahead of any render: quick key presses build on it, and only its save clears it.
  const latestOrder = useRef<number[] | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [newId, setNewId] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);

  const byId = new Map(doc.positions.map((p) => [p.id, p]));
  const positions: Position[] = pendingOrder
    ? pendingOrder.map((id) => byId.get(id)).filter((p): p is Position => p !== undefined)
    : doc.positions;
  const ids = positions.map((p) => p.id);

  const currentIds = () => latestOrder.current ?? doc.positions.map((p) => p.id);

  async function reorder(order: number[]) {
    const base = currentIds();
    if (order.length === base.length && order.every((id, i) => id === base[i])) return;
    latestOrder.current = order;
    setPendingOrder(order);
    try {
      await writes.save({ order });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      if (latestOrder.current === order) {
        latestOrder.current = null;
        setPendingOrder(null);
      }
    }
  }

  function step(id: number, delta: -1 | 1) {
    const base = currentIds();
    const next = movePosition(base, id, delta);
    if (!next) return;
    setAnnouncement(moveAnnouncement(base.indexOf(id) + 1, delta));
    void reorder(next);
  }

  async function add() {
    try {
      const before = new Set(ids);
      const next = await writes.save({ positions: [{ title: "", description: "", quantity: 1, unit: "Stk", unitPrice: 0 }] });
      setNewId(next.document.positions.find((p) => !before.has(p.id))?.id ?? null);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  // No mark where the drop would change nothing (right above or below the dragged row).
  const dropMark = (i: number): "before" | "after" | null => {
    if (dragging === null || dropAt === null) return null;
    const from = ids.indexOf(dragging);
    if (dropAt === from || dropAt === from + 1) return null;
    if (dropAt === i) return "before";
    return dropAt === ids.length && i === ids.length - 1 ? "after" : null;
  };

  const isInvoice = doc.type === "invoice";
  return (
    <section aria-labelledby="h-pos" className="rounded-xl border bg-card">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        <h2 id="h-pos" className="text-[0.9375rem] font-semibold">Positionen</h2>
        <span className="text-[0.8125rem] text-muted-foreground">Klick in einen Text, um ihn zu ändern. Die Vorschau markiert die Zeile mit.</span>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {isInvoice && (
            <>
              <Button variant="outline" size="sm" className="pointer-coarse:h-11" onClick={() => onOpenItems("entries")}>Aus Buchungen</Button>
              <Button variant="outline" size="sm" className="pointer-coarse:h-11" onClick={() => onOpenItems("shares")}>Ausgabe weiterverrechnen</Button>
            </>
          )}
          <Button variant="ghost" size="sm" className="pointer-coarse:h-11" onClick={add}><Plus />Position</Button>
        </div>
      </div>
      {positions.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">Noch keine Positionen.</p>
      ) : (
        <div role="list" onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropAt(null); }}>
          {positions.map((p, i) => (
            <PositionRow
              key={p.id}
              position={p}
              n={i + 1}
              offerNumber={offerNumber}
              highlighted={highlighted === p.id}
              autoFocus={newId === p.id}
              drop={dropMark(i)}
              save={writes.save}
              onHover={onHover}
              onFocusChange={onFocusChange}
              canMove={{ up: i > 0, down: i < positions.length - 1 }}
              onMove={(delta) => step(p.id, delta)}
              onDragStart={() => setDragging(p.id)}
              onDragOver={(e) => {
                if (dragging === null) return;
                e.preventDefault();
                const box = e.currentTarget.getBoundingClientRect();
                setDropAt(e.clientY < box.top + box.height / 2 ? i : i + 1);
              }}
              onDrop={() => {
                if (dragging !== null && dropAt !== null) {
                  const base = currentIds();
                  const from = base.indexOf(dragging);
                  void reorder(moved(base, dragging, dropAt > from ? dropAt - 1 : dropAt));
                }
                setDragging(null);
                setDropAt(null);
              }}
              onDragEnd={() => {
                setDragging(null);
                setDropAt(null);
              }}
            />
          ))}
        </div>
      )}
      <p aria-live="polite" className="sr-only">{announcement}</p>
    </section>
  );
}
